// AJAX add-to-cart for the custom (non-Horizon) product forms:
//   st-collection cards (.st-atc-form), lp-hero (.lp-atc-form), lp-cta snippet (.lp-cta-form).
// Reuses Horizon's OWN cart mechanism — no library added:
//   1. dispatch CartLinesUpdateEvent(action:'add') on <theme-drawer#cart-drawer> (or document).
//      Horizon's cart-drawer-component (auto-open), cart-icon, header-actions, and
//      cart-items-component all listen for this and react.
//   2. POST routes.cart_add_url with `sections` = the cart drawer's section id, so the
//      resolved promise carries rendered cart HTML that cart-items-component morphs in.
// Progressive enhancement: if this module (or fetch) fails, the plain <form action="/cart/add"
// method="post"> still submits and adds the item — never a dead button.
import { CartLinesUpdateEvent } from '@shopify/events';

const FORM_SELECTOR = '.st-atc-form, .lp-atc-form, .lp-cta-form';
const ERROR_MS = 3000;

/** The element Horizon scopes cart events to (see standard-actions-override.js). */
function eventTarget() {
  return document.querySelector('theme-drawer#cart-drawer') ?? document;
}

/**
 * Open the cart drawer via Horizon's own <theme-drawer> element. The theme's built-in
 * auto-open on add is gated by the `auto_open_cart_drawer` merchant setting, so the custom
 * ATC opens the drawer explicitly. Falls back to the /cart page when no drawer is present.
 */
function openCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer?.open) {
    drawer.open();
    return;
  }
  // No drawer on this page/template — send the shopper to the cart page instead.
  window.location.href = routes().cart_url ?? '/cart';
}

/** Reverse an optimistic open when the add ultimately fails. */
function closeCartDrawer() {
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (drawer?.close) drawer.close();
}

/** Collect the cart-drawer section id(s) so the add response returns rendered cart HTML. */
function cartSectionIds() {
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    const id = el.getAttribute('data-section-id');
    if (id && !ids.includes(id)) ids.push(id);
  });
  return ids.join(',');
}

/** Horizon exposes routes on a top-level `const Theme` (not on window). */
function routes() {
  return typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};
}

function setBusy(button, busy) {
  if (!button) return;
  button.disabled = busy;
  button.setAttribute('aria-busy', busy ? 'true' : 'false');
}

function flash(button, text, ms) {
  if (!button) return;
  const original = button.dataset.stLabel ?? button.textContent;
  button.dataset.stLabel = original;
  button.textContent = text;
  window.clearTimeout(button.__stTimer);
  button.__stTimer = window.setTimeout(() => {
    button.textContent = button.dataset.stLabel ?? original;
    button.removeAttribute('aria-busy');
  }, ms);
}

async function submitForm(form, button) {
  const formData = new FormData(form);
  const sections = cartSectionIds();
  if (sections) formData.set('sections', sections);

  const quantity = Number(formData.get('quantity')) || 1;
  const merchandiseId = String(formData.get('id') ?? '');

  const deferred = CartLinesUpdateEvent.createPromise();

  // Announce the add on the cart-drawer scope BEFORE the network call, exactly like
  // Horizon's product-form. The drawer's auto-open reacts to this; the resolved
  // promise below feeds it the fresh cart HTML.
  eventTarget().dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId, quantity }],
      promise: deferred.promise,
    })
  );

  // Optimistic open (like Horizon's own auto-open on add): surface the drawer
  // immediately so perceived click->drawer time is ~instant. The drawer shows its
  // current contents, then cart-items-component morphs in the fresh HTML when the
  // deferred promise resolves below. On a hard failure we close it again.
  openCartDrawer();

  try {
    // cart_add_url already carries the `.js` suffix (see snippets/scripts.liquid).
    const addUrl = routes().cart_add_url ?? '/cart/add.js';
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await response.json();

    // Shopify returns a `status`/`message` on error (sold out, unavailable, etc.)
    if (data.status || !response.ok) {
      const message = data.description || data.message || 'Could not add to cart';
      deferred.resolve({ cart: null, detail: { didError: true, source: 'st-atc' } });
      // Nothing was added — reverse the optimistic open and surface the reason.
      closeCartDrawer();
      flash(button, message.length > 40 ? 'Unavailable' : message, ERROR_MS);
      setBusy(button, false);
      return;
    }

    // Add succeeded (drawer is already open from the optimistic call above).
    // Refetch the cart (non-blocking) so the event carries true totals for cart-icon,
    // then resolve the deferred promise the drawer/cart-items are awaiting; the
    // rendered `sections` HTML morphs the drawer content in.
    setBusy(button, false);
    const cartUrl = routes().cart_url ?? '/cart';
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })
      .then((r) => r.json())
      .then((cart) => {
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: false,
            items: cart.items,
            sections: data.sections,
            source: 'st-atc',
            itemCount: quantity,
          },
        });
      })
      .catch((err) => deferred.reject(err));
  } catch (error) {
    // Network failure — the add never happened, so undo the optimistic open.
    deferred.reject(error);
    closeCartDrawer();
    flash(button, 'Try again', ERROR_MS);
    setBusy(button, false);
  }
}

// Bind exactly once — the module may be included by more than one section on a page.
if (!window.__stAtcBooted) {
  window.__stAtcBooted = true;
  document.addEventListener('submit', (event) => {
    const form = event.target instanceof HTMLElement ? event.target.closest(FORM_SELECTOR) : null;
    if (!form) return;
    event.preventDefault();

    const button = form.querySelector('button[type="submit"], .btn-cta, .btn');
    if (button?.disabled) return;
    setBusy(button, true);
    submitForm(form, button);
  });
}
